import { useState, useEffect, useRef } from "react";
import { Bell, Check, CheckCheck, ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [list, cnt] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data);
      setUnread(cnt.data.count);
    } catch (e) {/* silent */}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // poll every 60s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id, link) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch {}
    if (link) { setOpen(false); navigate(link); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); toast.success("All marked read"); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative w-9 h-9 rounded-xl bg-white border border-[#E8E6E1] flex items-center justify-center text-[#5C6660] hover:border-[#2A4D3E] hover:text-[#2A4D3E] transition"
      >
        <Bell size={16} strokeWidth={1.7} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D97757] text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notification-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] bg-white border border-[#E8E6E1] rounded-2xl shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E6E1]">
            <div className="font-heading font-semibold text-[#2A332C]">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="mark-all-read"
                className="text-xs text-[#2A4D3E] font-semibold flex items-center gap-1 hover:underline">
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-[#8E9992]">You're all caught up.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n.id, n.link)}
                data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2] transition ${n.read ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#D97757] mt-1.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2A332C] truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-[#5C6660] mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-[#8E9992] mt-1">{relativeTime(n.created_at)}</div>
                  </div>
                  {n.link && <ArrowUpRight size={13} className="text-[#8E9992] mt-1 flex-shrink-0" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
